import { SkillsApiResponse } from '../types';
import { API_BASE } from './api';

// Use backend proxy to avoid CORS issues with skills.sh
const getSkillsApiUrl = () => `${API_BASE}/spec-driven/external-skills`;

const CACHE_KEY = 'skills_sh_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: SkillsApiResponse;
  timestamp: number;
  key: string;
}

function getCacheKey(page: number, search: string): string {
  return `${page}_${search}`;
}

function getFromCache(page: number, search: string): SkillsApiResponse | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const entries: CacheEntry[] = JSON.parse(cached);
    const key = getCacheKey(page, search);
    const entry = entries.find(e => e.key === key);

    if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
      return entry.data;
    }
    return null;
  } catch {
    return null;
  }
}

function saveToCache(page: number, search: string, data: SkillsApiResponse): void {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    let entries: CacheEntry[] = cached ? JSON.parse(cached) : [];

    // Remove expired entries
    entries = entries.filter(e => Date.now() - e.timestamp < CACHE_TTL);

    // Add new entry
    const key = getCacheKey(page, search);
    entries = entries.filter(e => e.key !== key);
    entries.push({ key, data, timestamp: Date.now() });

    // Keep only last 20 entries
    if (entries.length > 20) {
      entries = entries.slice(-20);
    }

    localStorage.setItem(CACHE_KEY, JSON.stringify(entries));
  } catch {
    // Ignore cache errors
  }
}

export async function fetchExternalSkills(
  page: number = 1,
  search: string = ''
): Promise<SkillsApiResponse> {
  // Check cache first
  const cached = getFromCache(page, search);
  if (cached) {
    return cached;
  }

  // Build URL with query parameters
  const params = new URLSearchParams();
  params.set('page', String(page));
  if (search) {
    params.set('search', search);
  }

  const response = await fetch(`${getSkillsApiUrl()}?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch skills: ${response.status}`);
  }

  const data: SkillsApiResponse = await response.json();

  // Cache the result
  saveToCache(page, search, data);

  return data;
}

export function clearSkillsCache(): void {
  localStorage.removeItem(CACHE_KEY);
}
